import MyAvatar from '@/components/my-avatar';
import { useSessionContext } from '@/contexts/session/context';
import {
  ChatMessageCategory,
  ChatMessagePayloadType,
  ChatMessagePulled,
  type ChatMessage,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { ChatCategory, type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { useChatContext } from '@/contexts/chat/context';
import { useRefs } from '@/scripts/hooks';
import type { ChatGroupMember } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import { getAvatar } from '@/scripts/utils/genAvatar';
import { isMeMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/utils';
import MessageText from '../messageText';
import MessageWithExpireAnimate from '../messageWithExpireAnimate';
import MessageFile from '../messageFile';
import styles from './index.module.scss';
// import MessageFile from '../messageFile';

export interface MessageItemWrapperProps {
  message: ChatMessage;
  chat: Chat;
  merge?: boolean;
  children: React.ReactNode | ((displayTime: string) => React.ReactNode);
}

export function MessageItemWrapper({
  message,
  chat,
  merge,
  children,
}: MessageItemWrapperProps) {
  const { session } = useSessionContext();
  const { chatGroup, createOrActiveChat } = useChatContext();
  const [user, setUser] = useState<ChatGroupMember>();

  const isMe = useMemo(() => isMeMessage(message), [message]);

  const refs = useRefs({
    chat,
    chatGroup,
    session,
    message,
  });

  const avatarSrc = useMemo(() => {
    if (merge) {
      return '';
    }

    const { chat, session } = refs.current;
    let user_: ChatGroupMember | undefined;

    if (user) {
      user_ = user;
    } else {
      user_ = isMe ? session?.user : (chat.user as any);
    }

    return user_ ? getAvatar(user_.avatar, user_.name) : '';
  }, [isMe, merge, refs, user]);

  const isShowName = useMemo(() => {
    const { chat } = refs.current;
    return chat.category === ChatCategory.GROUP && !isMe;
  }, [isMe, refs]);

  const handleAvatarClick = useCallback(() => {
    if (isMe) {
      return;
    }
    createOrActiveChat({ userId: message.owner_id });
  }, [isMe, createOrActiveChat, message.owner_id]);

  useLayoutEffect(() => {
    (async () => {
      if (merge) {
        setUser(undefined);
        return;
      }
      const { chat, chatGroup } = refs.current;
      if (isShowName) {
        const [
          {
            args: [user],
          },
        ] = await chatGroup.getMembersByUserId([
          { id: chat.chat_id, args: [message.owner_id] },
        ]);
        setUser(user);
      }
    })();
  }, [isMe, isShowName, merge, message.owner_id, refs]);

  return (
    <div
      className={cls(
        styles.messageItem,
        isMe && styles.messageItemMe,
        merge && styles.messageItemMerge
      )}
    >
      {merge ? (
        <div className={styles.avatarPlaceholder} />
      ) : (
        <MyAvatar
          size="small"
          className={styles.avatar}
          src={avatarSrc}
          onClick={handleAvatarClick}
        />
      )}

      <MessageWithExpireAnimate message={message} chat={chat} isMe={isMe}>
        {(displayTime) => (
          <div className={styles.content}>
            {!merge && isShowName && (
              <div className={styles.userName}>{user?.name}</div>
            )}
            {typeof children === 'function' ? children(displayTime) : children}
          </div>
        )}
      </MessageWithExpireAnimate>
    </div>
  );
}

export default function MessageItem(params: Omit<MessageItemWrapperProps, 'children'>) {
  const { message, chat } = params;
  const { retryMessage } = useChatContext();

  const isMe = useMemo(
    () => message.category === ChatMessageCategory.SEND,
    [message.category]
  );

  return (
    <MessageItemWrapper {...params}>
      {(displayTime) => (
        <>
          {(() => {
            switch (message.payload_type) {
              case ChatMessagePayloadType.TEXT:
              case ChatMessagePayloadType.ENCRYPTED_TEXT:
                return (
                  <MessageText
                    message={message}
                    chat={chat}
                    isMe={isMe}
                    onClickStatus={() => retryMessage({ chat, message })}
                    displayTime={displayTime ?? ''}
                  />
                );
              case ChatMessagePayloadType.FILE:
              case ChatMessagePayloadType.IMAGE:
                return (
                  <MessageFile
                    message={message}
                    chat={chat}
                    isMe={isMe}
                    onClickStatus={() => retryMessage({ chat, message })}
                    displayTime={displayTime ?? ''}
                  />
                );
              default:
                break;
            }
          })()}
        </>
      )}
    </MessageItemWrapper>
  );
}

export function MessageStatusItem(
  params: Omit<MessageItemWrapperProps, 'children'> & { onClickRetry?: () => void }
) {
  const { message } = params;

  return (
    <MessageItemWrapper {...params}>
      {message.pulled === ChatMessagePulled.ERROR ? (
        <div>
          Message load error.
          <Button type="link" size="small" onClick={params.onClickRetry}>
            Retry
          </Button>
        </div>
      ) : (
        <Skeleton.Node active style={{ width: 100, height: 34 }}></Skeleton.Node>
      )}
    </MessageItemWrapper>
  );
}
